import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import {
  BrokerConfigDto,
  BrokerType,
  MessagingProtocol,
  MockService,
  TopicConfigDto,
} from '../../../../../../types/rest/mockServiceTypes';
import _ from 'lodash';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Button from 'react-bootstrap/Button';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../store/rootReducer';
import { setMessagingActiveTab } from '../../../../../../store/mockServices/mockServiceDetail/mockServiceDetailActions';
import BlazeInputDropdown, {
  BlazeDropdownItem,
} from '../../../../../../components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import { getEnumValueByString, isTemplate } from '../../../../../../utils/miscUtil';
import ComponentList from '../../../../../../components/lists/ComponentList/ComponentList';
import TopicConfig from './ListRenderComponents/TopicConfig';
import FormInputComponent from './ListRenderComponents/FormInputComponent';
import CheckBox from '../../../../../../components/ui/checkBox/CheckBox';
import { updateTemplateMessagingConfigTab } from '../../../../../../store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import FlowConfigurations from './FlowConfiguration/FlowConfigurations';
import MockPreferredPortCell from '../../Cells/MockPreferredPortCell';
import Card from 'react-bootstrap/Card';
import BzmIcon from '../../../../../../components/icons/BzmIcon';
import { confirm } from '../../../../../../components/ui/modal/BlazeConfirmationModal';
import { Configuration } from '../../../../../../types/rest/configurationsType';
import { showToastNotification } from '../../../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../../../store/ui/toasts/toastsTypes';
import { applyConfiguration } from '../../../../../../utils/mockServiceUtils';
import MsgTestConnectionModal from '../../MsgTestConnectionModal';

import { useIsMount } from '../../../../../../hooks/useIsMountHook';

interface MessagingConfigurationProps {
  mockService: MockService;
  onUpdate: (update: MockService, debounced?: boolean) => void;
  disabled?: boolean;
}

const ibmMqNative = {
  name: MessagingProtocol.IBM_MQ9_NATIVE.toString(),
  id: MessagingProtocol.IBM_MQ9_NATIVE,
};

const ibmMqJms = {
  name: MessagingProtocol.IBM_MQ9_JMS.toString(),
  id: MessagingProtocol.IBM_MQ9_JMS,
};

const activeMqClassic = {
  name: MessagingProtocol.ACTIVE_MQ_CLASSIC.toString(),
  id: MessagingProtocol.ACTIVE_MQ_CLASSIC,
};

const activeMqArtemis = {
  name: MessagingProtocol.ACTIVE_MQ_ARTEMIS.toString(),
  id: MessagingProtocol.ACTIVE_MQ_ARTEMIS,
};

const kafka = {
  name: MessagingProtocol.KAFKA.toString(),
  id: MessagingProtocol.KAFKA,
};

const messagingProtocols = [ibmMqNative, ibmMqJms, activeMqClassic, activeMqArtemis, kafka];

const embeddedBrokerType = { name: 'Embedded', id: BrokerType.EMBEDDED };
const externalBrokerType = { name: 'External', id: BrokerType.EXTERNAL };

export const getBrokerTypes = (): BlazeDropdownItem[] => {
  return [externalBrokerType, embeddedBrokerType];
};

export const getMessagingProtocols = (isEmbedded: boolean) => {
  if (isEmbedded) {
    return [activeMqArtemis, activeMqClassic, kafka];
  }
  return [ibmMqNative, ibmMqJms, activeMqClassic, activeMqArtemis, kafka];
};

const MessagingConfiguration = ({ mockService, onUpdate, disabled }: MessagingConfigurationProps) => {
  const [brokerConfig, setBrokerConfig] = useState<BrokerConfigDto>(mockService.brokerConfig);
  const configuration: Configuration = useSelector((state: ApplicationState) => {
    return _.find(state.configurations.configurations, config => config.id === mockService.configurationId);
  });
  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount) {
      // Update the brokerConfig state only if it has changed
      if (!_.isEqual(brokerConfig, mockService.brokerConfig)) {
        setBrokerConfig(mockService.brokerConfig);
      }
    }
  }, [mockService.brokerConfig]);

  const [isModalTestConnectionShown, setIsModalTestConnectionShown] = useState(false);

  const activeTab = useSelector((state: ApplicationState) => {
    if (!isTemplate(mockService)) {
      return state.mockServiceDetails.messagingConfig.activeTab;
    } else {
      return state.mockServiceTemplateDetail.messagingConfig.activeTab;
    }
  });
  const dispatch = useDispatch();

  const onChangeHandler = (value: any, field: keyof BrokerConfigDto, debounce?: boolean) => {
    const updatedBrokerConfig = { ...brokerConfig, [field]: value };
    const update = { ...mockService, brokerConfig: updatedBrokerConfig };
    setBrokerConfig(updatedBrokerConfig);
    onUpdate(update, debounce);
  };

  const onChangeActiveTabHandler = (tab: string) => {
    if (!isTemplate(mockService)) {
      dispatch(setMessagingActiveTab(tab));
    } else {
      dispatch(updateTemplateMessagingConfigTab(tab));
    }
  };

  const isEmbedded = mockService.brokerConfig?.embeddedBroker === true;
  const isActiveMq =
    mockService.messagingProtocol === MessagingProtocol.ACTIVE_MQ_ARTEMIS ||
    mockService.messagingProtocol === MessagingProtocol.ACTIVE_MQ_CLASSIC;
  const isKafka = mockService.messagingProtocol === MessagingProtocol.KAFKA;

  const buttonGroup = (
    <ButtonGroup className={'mb-4 position-relative'}>
      <Button
        variant={activeTab === 'basics' ? 'primary' : 'secondary'}
        onClick={() => onChangeActiveTabHandler('basics')}
      >
        Basics
      </Button>
      {!isKafka && (
        <Button
          variant={activeTab === 'queues' ? 'primary' : 'secondary'}
          onClick={() => onChangeActiveTabHandler('queues')}
        >
          Queues
        </Button>
      )}
      <Button
        variant={activeTab === 'topics' ? 'primary' : 'secondary'}
        onClick={() => onChangeActiveTabHandler('topics')}
      >
        Topics
      </Button>
      <Button
        variant={activeTab === 'flowConfigurations' ? 'primary' : 'secondary'}
        onClick={() => onChangeActiveTabHandler('flowConfigurations')}
      >
        Flows
      </Button>
    </ButtonGroup>
  );

  const getTopics = () => {
    const topics: TopicConfigDto[] = _.isNil(brokerConfig?.topics) ? [] : brokerConfig.topics;
    return (
      <ComponentList
        disabled={disabled}
        emptyListMessage={'There are no Topics defined'}
        list={topics}
        onAddNewItem={() => {
          const update = brokerConfig?.topics ? [...brokerConfig?.topics] : [];
          update.push({
            isDurable: false,
            name: 'NEW.TOPIC.' + _.size(brokerConfig?.topics),
            durableSubscriptionName: null,
          });
          onChangeHandler(update, 'topics');
        }}
        renderItem={TopicConfig}
        onListChange={topics => {
          onChangeHandler(topics, 'topics');
        }}
        label={'Topic Name'}
      />
    );
  };

  const getQueues = () => {
    const queues: string[] = brokerConfig?.queues ? _.map(brokerConfig?.queues, item => item.name) : [];
    return (
      <ComponentList
        disabled={disabled}
        list={queues}
        label={'Queue Name'}
        emptyListMessage={'There are no Queues defined'}
        onAddNewItem={() => {
          const update = brokerConfig?.queues ? [...brokerConfig?.queues] : [];
          update.push({
            name: 'NEW.QUEUE.' + _.size(brokerConfig?.queues),
          });
          onChangeHandler(update, 'queues');
        }}
        renderItem={FormInputComponent}
        onListChange={list => {
          const updatedQueues = _.map(list, item => ({
            name: item,
          }));
          onChangeHandler(updatedQueues, 'queues');
        }}
      />
    );
  };

  const getSubscriptions = () => {
    const subscriptions: string[] = brokerConfig?.subscriptions
      ? _.map(brokerConfig?.subscriptions, item => item.name)
      : [];
    return (
      <ComponentList
        disabled={disabled}
        label={'Subscription'}
        list={subscriptions}
        emptyListMessage={'There are no Subscriptions defined'}
        onAddNewItem={() => {
          const update = brokerConfig?.subscriptions ? [...brokerConfig?.subscriptions] : [];
          update.push({
            name: 'Subscription.' + _.size(brokerConfig?.subscriptions),
          });
          onChangeHandler(update, 'subscriptions');
        }}
        renderItem={FormInputComponent}
        onListChange={list => {
          const updatedQueues = _.map(list, item => ({
            name: item,
          }));
          onChangeHandler(updatedQueues, 'subscriptions');
        }}
      />
    );
  };

  const changeProtocolHandler = (item: BlazeDropdownItem) => {
    const messagingProtocol: any = getEnumValueByString(MessagingProtocol, item.id);
    const update = { ...mockService, messagingProtocol: messagingProtocol };
    onUpdate(update);
  };

  const getSelectedMessagingProtocol = () => {
    return _.find(messagingProtocols, item => item.name === mockService.messagingProtocol);
  };
  const getSelectedBrokerType = () => {
    //If brokerConfig is not defined, default to External
    if (_.isNil(mockService.brokerConfig?.embeddedBroker)) {
      return externalBrokerType;
    }

    if (isEmbedded) {
      return embeddedBrokerType;
    } else {
      return externalBrokerType;
    }
  };

  const margin = 'mb-4';

  const basicBrokerConfig = (
    <>
      <div className={margin}>
        <label htmlFor={'broker'}>Broker Type</label>
        <BlazeInputDropdown
          id={'brokerType'}
          showGroups={false}
          items={getBrokerTypes()}
          selected={getSelectedBrokerType()}
          onClick={item => {
            let embedded = false;
            if (item.id === BrokerType.EMBEDDED) {
              embedded = true;
            }

            const updatedConfig: BrokerConfigDto = {
              ...mockService.brokerConfig,
              embeddedBroker: embedded,
            };

            const ibmMqProtocols = [MessagingProtocol.IBM_MQ9_NATIVE, MessagingProtocol.IBM_MQ9_JMS];
            const shouldResetProtocol = embedded && ibmMqProtocols.includes(mockService.messagingProtocol);
            const update = {
              ...mockService,
              brokerConfig: updatedConfig,
              messagingProtocol: shouldResetProtocol
                ? MessagingProtocol.ACTIVE_MQ_CLASSIC
                : mockService.messagingProtocol,
            };
            onUpdate(update);
          }}
          isSearchable={false}
          isDefaultOptionSelectable={false}
          defaultOption={messagingProtocols[0]}
          disabled={disabled || mockService.status === 'RUNNING'}
        />
      </div>

      {/* Protocol Selection */}
      <div className={margin}>
        <label htmlFor={'protocol'}>Messaging Protocol</label>
        <BlazeInputDropdown
          id={'MessagingProtocolDropdown'}
          showGroups={false}
          items={getMessagingProtocols(isEmbedded)}
          selected={getSelectedMessagingProtocol()}
          onClick={changeProtocolHandler}
          isSearchable={false}
          isDefaultOptionSelectable={false}
          defaultOption={messagingProtocols[0]}
          disabled={disabled}
        />
      </div>

      {isEmbedded && (
        <div className={margin}>
          <label htmlFor={'preferredPort'}>Preferred Port</label>
          <MockPreferredPortCell
            key={'messagingPort'}
            preferredPort={_.get(mockService.metadata, 'preferredMessagingRunnerPort', '')}
            mockService={mockService}
            disabled={disabled || mockService.status === 'RUNNING'}
            id={'messagingPort'}
            type={'mockService'}
            onChange={(rowData: MockService, preferredPort: string) => {
              const update = _.cloneDeep(rowData);
              update.metadata.preferredMessagingRunnerPort = preferredPort;
              onUpdate(update, true);
            }}
            isEditable={true}
          />
        </div>
      )}

      {!isEmbedded && (
        <div className={margin}>
          <label htmlFor={'brokerUrl'}>Hostname</label>
          <Form.Control
            autoComplete={'off'}
            id={'brokerUrl'}
            placeholder={'Enter broker Hostname...'}
            value={brokerConfig?.hostname || ''}
            onChange={e => onChangeHandler(e.target.value, 'hostname', true)}
            disabled={disabled}
          />
        </div>
      )}

      {!isEmbedded && (
        <div className={margin}>
          <label htmlFor={'brokerPort'}>Port</label>
          <Form.Control
            type={'text'}
            autoComplete={'off'}
            id={'brokerPort'}
            placeholder={'Enter broker port...'}
            value={brokerConfig?.port}
            onChange={e => onChangeHandler(e.target.value, 'port', true)}
            disabled={disabled}
          />
        </div>
      )}

      {!isActiveMq && !isKafka && (
        <div className={margin}>
          <label htmlFor={'brokerChannel'}>Channel</label>
          <Form.Control
            autoComplete={'off'}
            id={'brokerChannel'}
            placeholder={'Enter broker Channel...'}
            value={brokerConfig?.channel}
            onChange={e => onChangeHandler(e.target.value, 'channel', true)}
            disabled={disabled}
          />
        </div>
      )}

      {!isActiveMq && !isKafka && (
        <div className={margin}>
          <label htmlFor={'brokerQueueManager'}>Queue Manager</label>
          <Form.Control
            autoComplete={'off'}
            id={'brokerQueueManager'}
            placeholder={'Enter broker Queue manager...'}
            value={brokerConfig?.queueManager}
            onChange={e => onChangeHandler(e.target.value, 'queueManager', true)}
            disabled={disabled}
          />
        </div>
      )}

      <div className={margin}>
        <label htmlFor={'brokerUserName'}>Username</label>
        <Form.Control
          autoComplete={'off'}
          id={'brokerUserName'}
          placeholder={'Enter username...'}
          value={brokerConfig?.username}
          onChange={e => onChangeHandler(e.target.value, 'username', true)}
          disabled={disabled}
        />
      </div>

      <div className={margin}>
        <label htmlFor={'brokerUserName'}>Password</label>
        <Form.Control
          className={brokerConfig?.password !== '${config.MSG_PASSWORD}' ? 'passwordInput' : undefined}
          autoComplete={'off'}
          type={'text'}
          placeholder={'Enter password'}
          id={'brokerUserName'}
          value={brokerConfig?.password}
          onChange={e => onChangeHandler(e.target.value, 'password', true)}
          disabled={disabled}
        />
      </div>

      <div className={margin}>
        <CheckBox
          label={'Use SSL Authentication'}
          checked={brokerConfig?.sslAuthentication}
          disabled={disabled}
          onCheck={() => {
            const value = _.isNil(brokerConfig?.sslAuthentication) ? true : !brokerConfig.sslAuthentication;
            onChangeHandler(value, 'sslAuthentication');
          }}
        />
      </div>

      {!isActiveMq && !isKafka && (
        <div className={margin}>
          <label htmlFor={'brokerChannel'}>Ssl CipherSuite</label>
          <Form.Control
            autoComplete={'off'}
            id={'sslCipherSuite'}
            placeholder={'Enter Ssl Cipher Suite...'}
            value={brokerConfig?.sslCipherSuite}
            onChange={e => onChangeHandler(e.target.value, 'sslCipherSuite', true)}
            disabled={disabled}
          />
        </div>
      )}

      {isKafka && (
        <>
          {!isEmbedded && (
            <div className={margin}>
              <label htmlFor={'kafkaAutoOffsetReset'}>Auto Offset Reset</label>
              <BlazeInputDropdown
                id={'kafkaAutoOffsetReset'}
                showGroups={false}
                items={[
                  { id: 'earliest', name: 'Earliest' },
                  { id: 'latest', name: 'Latest' },
                  { id: 'none', name: 'None' },
                ]}
                selected={
                  brokerConfig?.autoOffsetReset
                    ? {
                        id: brokerConfig.autoOffsetReset,
                        name:
                          brokerConfig.autoOffsetReset.charAt(0).toUpperCase() + brokerConfig.autoOffsetReset.slice(1),
                      }
                    : { id: 'none', name: 'None' }
                }
                onClick={item => onChangeHandler(item.id, 'autoOffsetReset')}
                isSearchable={false}
                isDefaultOptionSelectable={false}
                defaultOption={{ id: 'none', name: 'None' }}
                disabled={disabled}
              />
            </div>
          )}

          {isEmbedded && (
            <>
              <div className={margin}>
                <label htmlFor={'kafkaNumPartitions'}>Number of Partitions</label>
                <Form.Control
                  type={'number'}
                  autoComplete={'off'}
                  id={'kafkaNumPartitions'}
                  placeholder={'Enter number of partitions...'}
                  value={brokerConfig?.numPartitions ?? 1}
                  onChange={e => onChangeHandler(parseInt(e.target.value, 10) || 1, 'numPartitions', true)}
                  disabled={disabled}
                  min={1}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );

  const getContent = () => {
    switch (activeTab) {
      case 'basics':
        return basicBrokerConfig;
      case 'queues':
        return getQueues();
      case 'topics':
        return getTopics();
      case 'subscriptions':
        return getSubscriptions();
      case 'flowConfigurations':
        return (
          <FlowConfigurations
            disabled={disabled}
            mockService={mockService}
            onChange={flowConfig => {
              onChangeHandler(flowConfig, 'flowConfigurations');
            }}
          />
        );
      default:
        return '';
    }
  };

  const showApplyConfigModal = () => {
    confirm({
      confirmation: (
        <p>Are you sure you would like to apply the broker configuration to the Messaging Broker config?</p>
      ),
      yesOptionTitle: 'Apply',
      yesOptionVariant: 'primary',
      title: 'Apply Configuration',
    }).then(() => {
      const update = _.cloneDeep(mockService);
      update.brokerConfig = applyConfiguration(brokerConfig, configuration);
      onUpdate(update, false);
      dispatch(showToastNotification(ToastType.Success, 'Broker Configuration applied successfully'));
    });
  };
  const getApplyConfigButton = () => {
    return (
      <Card className={'p-5'}>
        <h4 className={'mb-3'}>Existing configuration available</h4>
        <p>
          Load Configuration This will load a previously saved configuration and automatically fill in the connection
          details such as hostname, port, username, and password. <br />
        </p>

        <Button
          variant={'primary'}
          onClick={() => {
            showApplyConfigModal();
          }}
        >
          Apply Configuration
        </Button>

        <p className={'alert alert-info mt-4'}>
          <BzmIcon variant={'icon-bzm-info alert-icon'} /> Any values you have already entered will be replaced.
        </p>
      </Card>
    );
  };

  const isConfigurationUsed = useMemo((): boolean => {
    if (!_.isNil(configuration) && !_.isNil(mockService.brokerConfig)) {
      return _.some(Object.keys(mockService.brokerConfig), key =>
        _.startsWith(mockService.brokerConfig[key], '${config.MSG_'),
      );
    }
    return false;
  }, [configuration, mockService.brokerConfig]);

  const testConfigButton = !isEmbedded && (
    <div className={'float-right text-right'}>
      <Button
        disabled={!mockService.messagingRunnerEnabled || mockService.isDraft === true}
        variant={'secondary'}
        onClick={() => setIsModalTestConnectionShown(true)}
      >
        <BzmIcon variant={'icon-bzm-ct-lock-alt1'} className={'icon-xs'} />
        Test Connection
      </Button>
      {mockService.isDraft === true && (
        <div
          className="text-muted small"
          style={{
            position: 'relative',
            top: '6px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Save Local Changes In Order To Test Connection.
        </div>
      )}
    </div>
  );

  return (
    <div>
      {isModalTestConnectionShown && (
        <MsgTestConnectionModal
          mockService={mockService}
          closeModalHandler={() => setIsModalTestConnectionShown(false)}
        />
      )}

      <Row>
        <Col md={activeTab !== 'flowConfigurations' ? 6 : 10}>
          {/* Header */}
          {buttonGroup}
          {activeTab === 'basics' && <>{testConfigButton}</>}
          {/* ./Header */}

          {getContent()}
        </Col>
        {!_.isNil(configuration) && !isConfigurationUsed && activeTab === 'basics' && (
          <Col md={6}>{getApplyConfigButton()}</Col>
        )}
      </Row>
    </div>
  );
};

export default MessagingConfiguration;
